import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TripTypeOptions } from '@com-delta-idp/mnx-capability-flight-shop-ui';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { formatErrorCode } from '../../adapters/utils/utils.adapter';
import { RetrieveSearchTripTypeCookieName } from '../../constants/common.constants';
import { BOOK_WIDGET_ERROR_CODES } from '../../constants/error-codes.constants';
import {
  RetrieveSearchData,
  RetrieveSearchMcResponse,
  RetrieveSearchOwRtResponse
} from '../../models/core-apis/retrieve-search.interfaces';
import { RetrieveSearchService } from '../../services/retrieve-search.service';
import { UtilsService } from '../../services/utils.service';
import * as fromErrorHandlerActions from '../actions/error-handler.actions';
import * as fromRetrieveSearchActions from '../actions/retrieve-search.actions';
import { getFlightShopTemplateType } from '../selectors/book-widget.selectors';
import {
  getRetrieveSearchMcCacheKey,
  shouldTriggerRetrieveSearchApi
} from '../selectors/retrieve-search.selectors';

@Injectable({ providedIn: 'root' })
export class RetrieveSearchEffects {
  loadRetrieveSearch$ = createEffect(
    (): Observable<Action> =>
      this.RetrieveSearchActions$.pipe(
        ofType(fromRetrieveSearchActions.LoadRetrieveSearch),
        concatLatestFrom(() => [
          this.store.select(shouldTriggerRetrieveSearchApi),
          this.store.select(getRetrieveSearchMcCacheKey)
        ]),
        filter(
          ([, shouldTriggerRetrieveSearchApiValue]) =>
            shouldTriggerRetrieveSearchApiValue
        ),
        switchMap(() => {
          const trip_type = this.utilsService.getCookies(
            RetrieveSearchTripTypeCookieName
          );
          if (trip_type?.toUpperCase() === TripTypeOptions.MULTI_CITY) {
            return of(fromRetrieveSearchActions.LoadMcRetrieveSearch());
          }
          return of(fromRetrieveSearchActions.LoadOwRtRetrieveSearch());
        })
      )
  );

  loadOwRtRetrieveSearch$ = createEffect(
    (): Observable<Action> =>
      this.RetrieveSearchActions$.pipe(
        ofType(fromRetrieveSearchActions.LoadOwRtRetrieveSearch),
        concatLatestFrom(() => this.store.select(getFlightShopTemplateType)),
        switchMap(([, flightShopTemplateType]) =>
          this.service.getRetrieveSearchOwRt(flightShopTemplateType).pipe(
            map((retrieveSearchOwRtResponse: RetrieveSearchOwRtResponse[]) => {
              let retrieveSearchDataList: RetrieveSearchData[] = [];

              if (retrieveSearchOwRtResponse.length) {
                retrieveSearchDataList = retrieveSearchOwRtResponse.map(
                  (data) => {
                    return {
                      ...data,
                      airports: [data.airports],
                      dates: [data.dates]
                    };
                  }
                );
              }

              return fromRetrieveSearchActions.LoadRetrieveSearchSuccess({
                retrieveSearchDataList
              });
            }),
            catchError((error: HttpErrorResponse) =>
              of(
                fromErrorHandlerActions.HandleError({
                  errorCode: formatErrorCode(
                    BOOK_WIDGET_ERROR_CODES.OW_RT_RETRIEVE_SEARCH_FAILURE,
                    error?.status
                  ),
                  error: BOOK_WIDGET_ERROR_CODES.OW_RT_RETRIEVE_SEARCH_FAILURE
                }),
                fromRetrieveSearchActions.LoadRetrieveSearchFailure()
              )
            )
          )
        )
      )
  );

  loadMcRetrieveSearch$ = createEffect(
    (): Observable<Action> =>
      this.RetrieveSearchActions$.pipe(
        ofType(fromRetrieveSearchActions.LoadMcRetrieveSearch),
        concatLatestFrom(() => [
          this.store.select(getRetrieveSearchMcCacheKey),
          this.store.select(getFlightShopTemplateType)
        ]),
        switchMap(([, retrieveSearchMcCacheKey, flightShopTemplateType]) =>
          this.service
            .getRetrieveSearchMc(
              retrieveSearchMcCacheKey,
              flightShopTemplateType
            )
            .pipe(
              map((retrieveSearchMcResponse: RetrieveSearchMcResponse) => {
                return fromRetrieveSearchActions.LoadRetrieveSearchSuccess({
                  retrieveSearchDataList: [retrieveSearchMcResponse.value]
                });
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromErrorHandlerActions.HandleError({
                    errorCode: formatErrorCode(
                      BOOK_WIDGET_ERROR_CODES.MC_RETRIEVE_SEARCH_FAILURE,
                      error?.status
                    ),
                    error: BOOK_WIDGET_ERROR_CODES.MC_RETRIEVE_SEARCH_FAILURE
                  }),
                  fromRetrieveSearchActions.LoadRetrieveSearchFailure()
                )
              )
            )
        )
      )
  );

  constructor(
    private RetrieveSearchActions$: Actions,
    private service: RetrieveSearchService,
    private store: Store,
    private utilsService: UtilsService
  ) {}
}
